// ====================================================================
// THE GROVE — the geography & exploration oasis, down the west road
// ====================================================================
// The second themed zone, and the mirror of Wordville on the opposite road:
// its terrain is a plateau mask in the heightfield (centred at (-44, 0)), its
// scatter props join the chunk system, and its buildings reuse the exact
// builders the village uses — passed in via ctx because zone CONTENT belongs
// out here, not in main.js.
//
// What's in it: four game huts wired to exploration minigames through the same
// slug machinery as the village (all four show the standard Coming Soon card
// until their embeds ship — add a slug to PLAYABLE_GAME_SLUGS in main.js to
// switch one live), a shimmering oasis pond ringed by palm trees, an
// explorer's outfitter, and a welcome sign. The Grove Keeper and her lost
// fennec (the critter-rescue quest) live here too, built by
// src/game/critterquest.js.
//
// Chromebook discipline: the pond is one water disc + one shore ring, and each
// palm merges its trunk and fronds into a single vertex-coloured geometry —
// the same one-draw-call-per-thing trick Wordville uses for its oxen.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../../core/tier.js';
import { PALETTE, BUILDING_SCHEMES } from '../../core/palette.js';
import { addObstacle } from '../collision.js';
import { heightAt } from '../heightfield.js';
import { markDynamic } from '../freezeStatics.js';

export const GROVE_CENTER = { x: -44, z: 0 };
const POND = { x: -44, z: 7, r: 2.4 };

let _zoneCtx = null;
let _pond = null;
let _pondGlint = null;
const _palms = [];   // { fronds, baseRotY, phase }

function tinted(geo, hex) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

// A palm tree: a leaning tapered trunk plus a fan of fronds, each merged into
// one geometry. The frond crown is its own group so updateGrove() can sway it.
function buildPalm(ctx, x, z, scale = 1) {
  const gy = heightAt(x, z);
  const TRUNK = PALETTE.wood, TRUNK_DK = PALETTE.woodDark, FROND = PALETTE.leafBase, FROND_DK = PALETTE.leafDark;

  // Trunk: stacked tapering segments with a gentle lean.
  const segs = [];
  let y = 0;
  for (let i = 0; i < 5; i++) {
    const r0 = (0.13 - i * 0.018) * scale;
    const r1 = (0.115 - i * 0.018) * scale;
    const h = 0.42 * scale;
    const seg = tinted(new THREE.CylinderGeometry(r1, r0, h, 6), i % 2 ? TRUNK_DK : TRUNK);
    seg.translate(Math.sin(i * 0.5) * 0.06 * scale * i, y + h / 2, 0);
    segs.push(seg);
    y += h;
  }
  const trunkMesh = new THREE.Mesh(mergeGeometries(segs, false),
    new THREE.MeshLambertMaterial({ vertexColors: true }));
  for (const g of segs) g.dispose();
  trunkMesh.castShadow = !LOW;

  // Frond crown: six drooping cones fanning from the trunk top.
  const fronds = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const frond = tinted(new THREE.ConeGeometry(0.12 * scale, 1.0 * scale, 5), i % 2 ? FROND : FROND_DK);
    frond.rotateZ(-1.05);
    frond.rotateY(a);
    frond.translate(Math.cos(a) * 0.42 * scale, 0, Math.sin(a) * 0.42 * scale);
    fronds.push(frond);
  }
  const frondMesh = new THREE.Mesh(mergeGeometries(fronds, false),
    new THREE.MeshLambertMaterial({ vertexColors: true }));
  for (const g of fronds) g.dispose();
  frondMesh.castShadow = !LOW;

  const crown = new THREE.Group();
  markDynamic(crown);   // updateGrove sways the crown; the trunk stays frozen
  crown.position.y = y + 0.05 * scale;
  crown.add(frondMesh);
  const tip = Math.sin(4 * 0.5) * 0.06 * scale * 4;   // match top trunk lean
  crown.position.x = tip;

  const group = new THREE.Group();
  group.add(trunkMesh);
  group.add(crown);
  group.position.set(x, gy, z);
  group.rotation.y = Math.random() * Math.PI * 2;
  ctx.propsGroup.add(group);

  // A slim trunk obstacle so kids bonk the palm instead of walking through it.
  addObstacle({ minX: x - 0.18, maxX: x + 0.18, minY: gy, maxY: gy + 2 * scale, minZ: z - 0.18, maxZ: z + 0.18 });

  _palms.push({ crown, baseRotY: group.rotation.y, phase: Math.random() * Math.PI * 2 });
}

// The oasis pond: a translucent water disc on a sandy shore ring, conformed to
// sit just above the (flat, plateaued) Grove ground.
function buildPond(ctx) {
  const gy = heightAt(POND.x, POND.z);

  const shore = new THREE.Mesh(
    new THREE.RingGeometry(POND.r, POND.r + 0.8, 28),
    new THREE.MeshLambertMaterial({ color: PALETTE.sand })
  );
  shore.rotation.x = -Math.PI / 2;
  shore.position.set(POND.x, gy + 0.015, POND.z);
  ctx.propsGroup.add(shore);

  const water = new THREE.Mesh(
    new THREE.CircleGeometry(POND.r, 28),
    new THREE.MeshLambertMaterial({ color: PALETTE.water, transparent: true, opacity: 0.82 })
  );
  water.rotation.x = -Math.PI / 2;
  water.position.set(POND.x, gy + 0.03, POND.z);
  markDynamic(water);   // updateGrove breathes its scale
  ctx.propsGroup.add(water);
  _pond = water;

  _pondGlint = new THREE.Mesh(
    new THREE.RingGeometry(POND.r * 0.42, POND.r * 0.48, 28, 1, 0.35, Math.PI * 1.15),
    new THREE.MeshBasicMaterial({ color: 0xeaffff, transparent: true, opacity: 0.38, depthWrite: false, side: THREE.DoubleSide })
  );
  _pondGlint.rotation.x = -Math.PI / 2;
  _pondGlint.rotation.z = -0.25;
  _pondGlint.position.set(POND.x - 0.28, gy + 0.05, POND.z - 0.12);
  markDynamic(_pondGlint);   // updateGrove wobbles its rotation
  ctx.propsGroup.add(_pondGlint);

  const smallGlint = new THREE.Mesh(
    new THREE.RingGeometry(POND.r * 0.18, POND.r * 0.22, 20, 1, -0.15, Math.PI * 0.82),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.24, depthWrite: false, side: THREE.DoubleSide })
  );
  smallGlint.rotation.x = -Math.PI / 2;
  smallGlint.rotation.z = 0.55;
  smallGlint.position.set(POND.x + 0.58, gy + 0.055, POND.z + 0.42);
  ctx.propsGroup.add(smallGlint);

  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + (i % 2) * 0.18;
    const r = POND.r + (i % 3 === 0 ? 0.55 : 0.22);
    const x = POND.x + Math.cos(a) * r;
    const z = POND.z + Math.sin(a) * r;
    if (i % 3 === 0) ctx.buildGrassTuft?.(x, z, 0.65 + (i % 2) * 0.18);
    else ctx.buildLilyPad?.(POND.x + Math.cos(a) * (POND.r * 0.55), POND.z + Math.sin(a) * (POND.r * 0.55), 0.75 + (i % 2) * 0.18, a);
  }
}

// Called from the main loop while in play mode.
export function updateGrove(dt, t, playerPos) {
  if (!_zoneCtx) return;
  // The oasis dozes when nobody's near the zone (the Wordville herd guard).
  const dx = playerPos.x - GROVE_CENTER.x, dz = playerPos.z - GROVE_CENTER.z;
  if (dx * dx + dz * dz > 2025) return;            // asleep past ~45 units

  if (_pond) _pond.scale.setScalar(1 + Math.sin(t * 1.3) * 0.012);
  if (_pondGlint) {
    _pondGlint.rotation.z = -0.25 + Math.sin(t * 0.8) * 0.08;
    _pondGlint.material.opacity = 0.3 + Math.sin(t * 1.6) * 0.08;
  }
  for (const p of _palms) {
    p.crown.rotation.z = Math.sin(t * 1.1 + p.phase) * 0.06;
    p.crown.rotation.y = p.baseRotY + Math.sin(t * 0.7 + p.phase) * 0.04;
  }
}

export function buildGrove(ctx) {
  _zoneCtx = ctx;
  const { x: CX } = GROVE_CENTER;

  // ---- The road in: continue the west road from where its stones ended,
  // then a plaza spine with spurs to each doorway (mirror of Wordville).
  ctx.buildPath([[-27, 0], [-31, 0], [-35, 0], [-38.5, 0]]);
  ctx.buildPath([[-38.5, 0], [-41.5, 0], [-44.5, 0], [-48, 0]]);
  ctx.buildPath([[-40.5, 0], [-40.5, -2.4]]);      // Compass Quest
  ctx.buildPath([[-47.5, 0], [-47.5, -2.4]]);      // Map Maker
  ctx.buildPath([[-47.5, 0.4], [-47.5, 2.6]]);     // World Trek
  ctx.buildPath([[-40.5, 0.4], [-40.5, 2.6]]);     // Landmark Hunt
  ctx.buildPath([[-44, 0.4], [-44, 4.4]]);         // up to the pond

  // Daisies along the walk in — little colour beats on the journey.
  const groveFlowers = [PALETTE.sageSoft, PALETTE.skyBlueSoft, PALETTE.cream, PALETTE.goldSoft];
  ctx.buildFlowerPatch(-30.4, 1.7, 5, 0.55, groveFlowers);
  ctx.buildFlowerPatch(-33.8, -1.8, 5, 0.55, groveFlowers);

  // ---- Welcome sign at the zone entrance, and the signpost home.
  ctx.addHutSign(-38.2, 1.7, 'THE GROVE', { x: -38.2, y: 0, z: 1.7, yaw: -0.60 });
  ctx.buildSignpost(-37.6, -1.5, [{ text: 'CHOCKYTOWN', yaw: -0.6 }]);
  ctx.addBumpZone(-38.2, 1.7, 1.3, 'grove-sign', (sourceId) => {
    ctx.showBumpMsg('WELCOME TO THE GROVE!', 'A cool green oasis for explorers 🧭', 4, sourceId);
  });

  // ---- Game huts (doors face +z, same as the village). Geography &
  // exploration theme; all four are Coming Soon until their slugs are added to
  // PLAYABLE_GAME_SLUGS in main.js. Rooftop signs angle toward the camera.
  //                     x      z     w    d    wallH wall      roof      smoke  name            slug             rooftop sign
  ctx.buildGameHut(-47.5, -4.2, 2.4, 2.2, 1.5, BUILDING_SCHEMES.groveCream.wall, BUILDING_SCHEMES.groveCream.roof, true,  'MAP MAKER',     'map-maker',     { x: -47.5, y: 1.30, z: -4.2, yaw: -0.60 });
  ctx.buildGameHut(-40.5, -4.2, 2.2, 2.4, 1.4, BUILDING_SCHEMES.groveSage.wall, BUILDING_SCHEMES.groveSage.roof, false, 'COMPASS QUEST', 'compass-quest', { x: -40.5, y: 1.20, z: -4.2, yaw: -0.60 });
  ctx.buildGameHut(-47.5,  4.2, 2.4, 2.0, 1.6, BUILDING_SCHEMES.groveSky.wall, BUILDING_SCHEMES.groveSky.roof, true,  'WORLD TREK',    'world-trek',    { x: -47.5, y: 1.40, z:  4.2, yaw: -0.60 });
  ctx.buildGameHut(-40.5,  4.2, 2.2, 2.2, 1.5, BUILDING_SCHEMES.popSafari.wall, BUILDING_SCHEMES.popSafari.roof, false, 'LANDMARK HUNT', 'landmark-hunt', { x: -40.5, y: 1.30, z:  4.2, yaw: -0.60 });

  // ---- The explorer's outfitter: the zone's hero building, hinting at the
  // oasis/explorer gear now stocked in Chocky's Closet. Handmade kit model
  // when it loaded; the procedural big house otherwise.
  if (!ctx.buildKitStructure || !ctx.buildKitStructure('house_big', -44, -8.4, { name: 'Outfitter' })) {
    ctx.buildHouse(-44, -8.4, 4.6, 3.0, 2.4, PALETTE.sand, PALETTE.woodDark, false);
  }
  ctx.addHutSign(-44, -8.4, "OUTFITTER", { x: -44, y: 2.25, z: -8.4, yaw: -0.60 });
  ctx.addBumpZone(-44, -6.6, 1.6, 'grove-outfitter', (sourceId) => {
    ctx.showBumpMsg('THE OUTFITTER 🧭', 'Explorer gear stocked over in Chocky\'s Closet!', 4, sourceId);
  });

  // ---- The oasis pond and its ring of palms — the heart of the Grove.
  buildPond(ctx);
  buildPalm(ctx, CX - 3.2, POND.z - 0.6, 1.15);
  buildPalm(ctx, CX + 3.0, POND.z - 0.4, 1.0);
  buildPalm(ctx, CX - 2.4, POND.z + 2.6, 0.9);
  buildPalm(ctx, CX + 2.8, POND.z + 2.2, 1.1);
  buildPalm(ctx, CX + 0.4, POND.z + 3.4, 1.05);

  // ---- Dressing: rocks, reedy bushes, flowers, mushrooms, a lantern or two.
  ctx.buildLantern(-42, -1.6, false);
  ctx.buildLantern(-46, 1.6, false);
  ctx.buildFlowerPatch(-42.6, 1.6, 7, 0.8, groveFlowers);
  ctx.buildFlowerPatch(-45.6, -1.7, 7, 0.8, groveFlowers);
  ctx.buildFlowerPatch(-49.3, 0.6, 6, 0.7, groveFlowers);
  ctx.buildBush(-41.0, 6.0, 0.9, true);
  ctx.buildBush(-47.4, 6.4, 0.85, true);
  ctx.buildBush(-49.6, -1.4, 0.8);
  ctx.buildRock(-50.4, 4.2, 0.9);
  ctx.buildRock(-37.8, 5.4, 0.8);
  ctx.buildRock(-45.8, 9.6, 0.45);
  ctx.buildRock(-42.1, 8.9, 0.38);
  ctx.buildGrassTuft(-46.2, 9.1, 0.55);
  ctx.buildGrassTuft(-41.8, 7.9, 0.5);
  ctx.buildRock(-52.0, -6.5, 1.1);                 // the rocks the lost pet hides by
  ctx.buildMushroomCluster(-38.6, 7.0, 3);
  ctx.buildMushroomCluster(-50.8, 2.0, 2);
  ctx.buildTree(-37.2, -6.0, 0.95);
  ctx.buildTree(-51.6, 8.0, 1.0);
}
